// ProvenanceTag — glanceable "who last touched this, is anything pending", pure.
//
// .claude/research/design/03 (coexistence): every element can answer "who last authored
// this, and is anything pending here?" via the authoredBy metadata and the
// pending layer — the spatial read-wear/edit-wear lineage the research values,
// not a log to reconstruct. This tag is that answer made quiet and spatial: a
// small dot + label in the element's gutter, agent-authored content wearing the
// agent colour.
//
// Two discriminants drive it, both structural, neither a meaning-swapping flag:
//   • `authoredBy: Provenance` — human vs agent (its own icon + colour).
//   • `pending` — the PRESENCE of an agent proposal on this element is itself
//     the pending signal (.claude/research/design/03: an agent edit lands pending until
//     approved). Passed as the proposal (or its absence), not a bare boolean:
//     presence is the state.
//
// Pure/props-driven: provenance + pending in; nothing else. Non-interactive
// read-wear, so no role/target-size — it is a label, not a control.

import { useLingui } from "@lingui/react/macro";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import type { PendingProposal, Provenance } from "../types.js";
import "./ProvenanceTag.css";

export type ProvenanceTagProps = {
    readonly authoredBy: Provenance;
    // The pending agent proposal on this element, if any. Presence ⇒ pending.
    readonly pending?: PendingProposal;
};

const humanGlyph = <Icon name="person" size="xs" />;

// The single AI glyph (ai-transparency §3/§5 — unified from the old star split
// so the editor tag, the material badge and the marker all speak one symbol).
const agentGlyph = <Icon name="sparkle" size="xs" />;

export const ProvenanceTag = ({ authoredBy, pending }: ProvenanceTagProps) => {
    const { t } = useLingui();
    // Suppress the tag on a clean HUMAN block (subtractive audit 2026-08-19):
    // a "You" label on every human-authored, non-pending element is N labels
    // carrying zero information — noise, not scent. The AGENT mark (the actual
    // transparency signal) and the PENDING layer always render; only the
    // redundant human-default is dropped.
    if (authoredBy.type !== "agent" && pending === undefined) return null;
    // The author atoms — a match keeps the icon/label/colour class concrete per
    // variant (agent wears the agent colour; human is a quiet neutral).
    const author =
        authoredBy.type === "agent"
            ? {
                  glyph: agentGlyph,
                  label: authoredBy.name ?? t`Agent`,
                  cls: "bilda-provenance-tag--agent",
              }
            : {
                  glyph: humanGlyph,
                  label: authoredBy.name ?? t`You`,
                  cls: "bilda-provenance-tag--human",
              };

    return (
        <span className={`bilda-provenance-tag ${author.cls}`}>
            <span className="bilda-provenance-tag__icon" aria-hidden="true">
                {author.glyph}
            </span>
            <Text as="span" size="xs" tone="faint">
                {author.label}
            </Text>
            {pending ? (
                // The pending layer, made spatial: a quiet dot + word in the
                // agent colour. Its presence is the whole signal.
                <span className="bilda-provenance-tag__pending">
                    <span
                        className="bilda-provenance-tag__pending-dot"
                        aria-hidden="true"
                    />
                    <Text as="span" size="xs">
                        {t`Pending`}
                    </Text>
                </span>
            ) : null}
        </span>
    );
};
